import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Layers, GripVertical, Check, Search, Eye, EyeOff } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { useCatalog } from "../../context/CatalogContext";
import api from "../../api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { useToast } from "../../hooks/use-toast";

const inputCls = "w-full h-10 px-3 rounded-lg bg-secondary border border-border text-sm outline-none focus:border-brand-500/60 focus:ring-2 focus:ring-brand-500/20 transition-all";
const empty = { title: "", subtitle: "", order: 1, active: true, bookIds: [] };

export default function AdminCollections() {
  const { t } = useLang();
  const { adminBooks } = useCatalog();
  const { toast } = useToast();
  const [collections, setCollections] = useState([]);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");

  const load = () => api.get("/admin/collections").then((r) => setCollections(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...editing, order: parseInt(editing.order) || 0 };
    try {
      if (editing.id) await api.put(`/admin/collections/${editing.id}`, payload);
      else await api.post("/admin/collections", payload);
      toast({ title: editing.id ? "Collection updated" : "Collection created" });
      setEditing(null);
      load();
    } catch (e) {
      toast({ title: "Could not save collection", variant: "destructive" });
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this collection?")) return;
    await api.delete(`/admin/collections/${id}`);
    load();
  };

  const toggleBook = (bookId) => {
    setEditing((prev) => {
      const has = prev.bookIds.includes(bookId);
      return { ...prev, bookIds: has ? prev.bookIds.filter((b) => b !== bookId) : [...prev.bookIds, bookId] };
    });
  };

  const filteredBooks = adminBooks.filter((b) => b.title_en.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="animate-float-up">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Collections</h1>
          <p className="text-sm text-muted-foreground">Curated shelves shown on the home page</p>
        </div>
        <Button onClick={() => setEditing({ ...empty, order: collections.length + 1 })} className="bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold rounded-full">
          <Plus className="w-4 h-4 mr-1.5" /> New collection
        </Button>
      </div>

      <div className="space-y-3">
        {collections.length === 0 && <p className="text-sm text-muted-foreground">No collections yet.</p>}
        {collections.map((c) => (
          <div key={c.id} className="flex items-center gap-4 p-4 rounded-2xl border border-border bg-card/50">
            <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="w-7 h-7 rounded-lg bg-brand-500/15 text-brand-400 flex items-center justify-center text-xs font-bold shrink-0">{c.order}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-semibold truncate">{c.title}</p>
                <Badge variant="outline" className={c.active ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-secondary text-muted-foreground border-border"}>
                  {c.active ? <Eye className="w-3 h-3 mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
                  {c.active ? "live" : "hidden"}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground truncate">{c.subtitle} · {c.bookIds.length} audiobooks</p>
              <div className="flex -space-x-2 mt-2">
                {c.books.slice(0, 8).map((b) => (
                  <img key={b.id} src={b.cover} alt="" className="w-8 h-11 rounded object-cover border-2 border-card" />
                ))}
              </div>
            </div>
            <button onClick={() => setEditing({ ...c })} className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
              <Pencil className="w-4 h-4" />
            </button>
            <button onClick={() => remove(c.id)} className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-red-400 hover:bg-secondary transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Layers className="w-5 h-5 text-brand-400" />
              {editing?.id ? "Edit collection" : "New collection"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">Title</label>
                  <input className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="e.g. New & Noteworthy" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">Order</label>
                  <input type="number" className={inputCls} value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Subtitle</label>
                <input className={inputCls} value={editing.subtitle} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} placeholder="Short description" />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                <span className="text-sm">{editing.active ? "Live on home page" : "Hidden"}</span>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-muted-foreground">Audiobooks ({editing.bookIds.length} selected)</label>
                  <div className="relative w-48">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search" className="w-full h-8 pl-8 pr-2 rounded-lg bg-secondary border border-border text-xs outline-none" />
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto space-y-1 rounded-xl border border-border p-2">
                  {filteredBooks.map((b) => {
                    const sel = editing.bookIds.includes(b.id);
                    return (
                      <button key={b.id} onClick={() => toggleBook(b.id)} className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${sel ? "bg-brand-500/10" : "hover:bg-secondary"}`}>
                        <img src={b.cover} alt="" className="w-8 h-11 rounded object-cover" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{b.title_en}</p>
                          <p className="text-xs text-muted-foreground truncate">{b.author}</p>
                        </div>
                        <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${sel ? "bg-brand-500 border-brand-500 text-brand-950" : "border-border"}`}>
                          {sel && <Check className="w-3.5 h-3.5" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
